/**
 * 내부 기간 파싱·표기 — 한국어("9년 2개월 3일") ↔ 저장 포맷(PnYnMnD).
 * 정수 기간의 단위는 보존(30일≠1개월), 소수점 입력만 환산(1년=12개월, 1개월=30일,
 * 일은 round). 기존 `ixd-editor/value-validation.ts`의 duration 로직 복제.
 */

export interface DurationParts {
  years: number;
  months: number;
  days: number;
}

const PLAIN_DECIMAL_PATTERN = /^\d+(?:\.\d+)?$/;
const STORED_DURATION_PATTERN = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?$/;

export const formatDurationPartsForDisplay = ({
  years,
  months,
  days,
}: DurationParts): string => {
  const parts: string[] = [];
  if (years > 0) parts.push(`${years}년`);
  if (months > 0) parts.push(`${months}개월`);
  if (days > 0) parts.push(`${days}일`);
  return parts.length > 0 ? parts.join(" ") : "0일";
};

const normalizeDurationParts = ({
  years,
  months,
  days,
}: DurationParts): DurationParts => {
  const extraMonthsFromDays = Math.floor(days / 30);
  const normalizedDays = days % 30;
  const totalMonths = months + extraMonthsFromDays;
  const extraYearsFromMonths = Math.floor(totalMonths / 12);

  return {
    years: years + extraYearsFromMonths,
    months: totalMonths % 12,
    days: normalizedDays,
  };
};

export const serializeDurationParts = ({
  years,
  months,
  days,
}: DurationParts): string => {
  const serialized = [
    years > 0 ? `${years}Y` : "",
    months > 0 ? `${months}M` : "",
    days > 0 ? `${days}D` : "",
  ].join("");

  return serialized ? `P${serialized}` : "P0D";
};

export function parseStoredDurationValue(value: string): DurationParts | null {
  const trimmed = value.trim();

  if (trimmed === "") return null;

  const matched = trimmed.match(STORED_DURATION_PATTERN);
  if (!matched) return null;

  const years = matched[1] ? Number(matched[1]) : 0;
  const months = matched[2] ? Number(matched[2]) : 0;
  const days = matched[3] ? Number(matched[3]) : 0;

  if (![years, months, days].every(Number.isInteger)) return null;
  if (years < 0 || months < 0 || days < 0) return null;
  if (years === 0 && months === 0 && days === 0 && trimmed !== "P0D") {
    return null;
  }

  return { years, months, days };
}

const convertDurationNumbersToParts = ({
  years = 0,
  months = 0,
  days = 0,
}: {
  years?: number;
  months?: number;
  days?: number;
}): DurationParts | null => {
  if (
    ![years, months, days].every(
      (value) => Number.isFinite(value) && value >= 0,
    )
  ) {
    return null;
  }

  const wholeYears = Math.floor(years);
  const monthsFromYearFraction = (years - wholeYears) * 12;

  const totalMonths = months + monthsFromYearFraction;
  const wholeMonths = Math.floor(totalMonths);
  const daysFromMonthFraction = (totalMonths - wholeMonths) * 30;

  const totalDays = days + daysFromMonthFraction;
  const roundedDays = Math.round(totalDays);

  return normalizeDurationParts({
    years: wholeYears,
    months: wholeMonths,
    days: roundedDays,
  });
};

/**
 * 자유 입력 기간 파싱. 숫자만이면 연 단위. 단위 중복("1년 2년")·미커버 토큰("약 3년")·
 * 저장 포맷 직접 입력("P9Y2M")은 실패(null).
 */
export function parseEditedDurationValue(value: string): DurationParts | null {
  const trimmed = value.trim();

  if (trimmed === "") return null;

  const compact = trimmed.replace(/\s+/g, "");

  if (PLAIN_DECIMAL_PATTERN.test(compact)) {
    const years = Number(compact);
    return compact.includes(".")
      ? convertDurationNumbersToParts({ years })
      : { years, months: 0, days: 0 };
  }

  const tokenPattern = /(\d+(?:\.\d+)?)(년|개월|일)/g;
  const tokens = [...compact.matchAll(tokenPattern)];

  if (tokens.length === 0) return null;

  const matchedText = tokens.map((token) => token[0]).join("");
  if (matchedText !== compact) return null;

  let years: number | undefined;
  let months: number | undefined;
  let days: number | undefined;

  for (const [, numericText, unit] of tokens) {
    const parsed = Number(numericText);

    if (!Number.isFinite(parsed) || parsed < 0) return null;

    switch (unit) {
      case "년":
        if (years !== undefined) return null;
        years = parsed;
        break;
      case "개월":
        if (months !== undefined) return null;
        months = parsed;
        break;
      case "일":
        if (days !== undefined) return null;
        days = parsed;
        break;
      default:
        return null;
    }
  }

  const durationParts = {
    years: years ?? 0,
    months: months ?? 0,
    days: days ?? 0,
  };
  const includesDecimal = tokens.some((token) => token[1].includes("."));

  // 정수 기간의 단위는 그대로 보존한다. 예를 들어 30일과 1개월은
  // 달력 기준으로 같은 기간이 아니므로 소수점 입력이 있을 때만 환산한다.
  return includesDecimal
    ? convertDurationNumbersToParts(durationParts)
    : durationParts;
}
